import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { SmartphoneNfc } from 'lucide-react';
import { FadeInImage } from 'components/UI/FadeInImage';

interface WhereToScanPanelProps {
  /**
   * VRG-323 — the brand's own "where is the tag on this item" copy, replacing the
   * generic body sentence. Arrives ALREADY RESOLVED from the API (product value,
   * else company default, else null), so there is no product-vs-company fallback
   * to re-implement here.
   */
  instructions?: string | null;
  /**
   * VRG-323 — absolute public URL of a still or clip showing where to scan,
   * rendered in place of the glyph circle. Same already-resolved contract as
   * `instructions`.
   */
  mediaUrl?: string | null;
}

/** Media types the drawer will render. Anything else is treated as absent. */
type MediaKind = 'image' | 'video';

/**
 * VRG-323: the extension is the ONLY signal available.
 *
 * Nothing in this stack transcodes, probes, or records a content type — a brand's
 * upload is served raw — so there is no metadata to consult and the URL's own
 * suffix decides. An unrecognised suffix returns null rather than guessing, which
 * routes it to the same glyph fallback as a broken load: showing nothing beats
 * mounting an <img> at a .pdf or an empty <video> the user can only stare at.
 *
 * The query string and fragment are stripped first, because a signed or
 * cache-busted URL (`…/tag.mp4?X-Goog-Signature=…`) still ends in the query, not
 * the extension.
 */
const mediaKindOf = (url: string): MediaKind | null => {
  const path = url.split(/[?#]/)[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp)$/.test(path)) return 'image';
  if (/\.(mp4|mov|webm)$/.test(path)) return 'video';
  return null;
};

/**
 * Sized for a 375px phone sheet. `DrawerPopup`'s bottom sheet is capped at
 * `maxHeight: 90vh` and is the scroll owner, so a tall portrait upload left
 * unbounded would push the heading and body below the fold and turn the whole
 * sheet into a scroller for a single picture. 240px keeps media + heading + copy
 * on one screen at 812px tall, and `contain` means a portrait clip letterboxes
 * instead of being cropped through the very detail it exists to show.
 */
const MEDIA_MAX_HEIGHT = 240;

/**
 * The media's footprint, RESERVED before the media exists (§19 footprint parity).
 *
 * An `<img>` with no intrinsic size is zero-height until its bytes arrive, so the
 * heading and the instruction below it started high and were shoved down the moment
 * the photo decoded — reported as "the image slide down. We should have space for the
 * image to fill - not flash in." `FadeInImage` already ramps opacity, but a fade
 * cannot fix this: the element was still zero-height while transparent, so the reveal
 * and the reflow happened together and the reflow is what the eye caught.
 *
 * A FIXED height rather than a min-height or an aspect-ratio guess: the aspect of a
 * brand's upload is unknowable before it loads, so anything derived from the image
 * still moves when the real one arrives. 240 is the cap the media was already limited
 * to, so this reserves exactly the worst case and never more.
 *
 * The slot is shared by the image and the video branch on purpose (§36) — two copies
 * of a reservation is how the two tiers drift into different heights, which is the
 * one thing §52's fixed step height cannot absorb.
 *
 * `flexShrink: 0` is load-bearing: this is a column flex child, and without it the
 * fixed height is merely a suggestion the layout can compress, which reintroduces the
 * shift it exists to prevent.
 */
const MediaSlot = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={theme => ({
      width: '100%',
      height: MEDIA_MAX_HEIGHT,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      borderRadius: '8px',
      // Neutral fill, kept after load rather than only during it. A portrait clip
      // letterboxes inside a 375px-wide slot (`contain`, so the detail the media
      // exists to show is never cropped), and the fill turns those bars into a
      // deliberate frame instead of two strips of page showing through.
      backgroundColor: theme.palette.grey[50],
    })}
  >
    {children}
  </Box>
);

/** Fit inside `MediaSlot` without overflowing it, whatever the upload's aspect is. */
const MEDIA_FIT = {
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  display: 'block',
} as const;

/**
 * VRG-323: the "Where to scan?" content, in three tiers.
 *
 * A PANEL, not a drawer (working-conventions §52). It used to be a third stacked bottom
 * sheet — Gift form (1) -> Confirm gift / scan prompt (2) -> Where to scan (3) — which
 * put the item the user was actually gifting behind two scrims, and left `Cancel` and
 * `Done` on screen together with nothing to say which unwound what. It is now a STEP
 * inside ScanTag's own sheet, which is the same fix ScanTag already applied to its
 * success state (see the `successTitle` docstring there); this is that fix reaching the
 * help side of the scan.
 *
 * So this file renders BODY CONTENT ONLY. It owns no sheet, no header, and no dismissal
 * — ScanTag supplies all three, once.
 *
 *   1. Brand media (`mediaUrl`)      -> the still/clip replaces the glyph circle.
 *   2. Brand copy (`instructions`)   -> replaces the generic body sentence.
 *   3. Neither                        -> the original VERA explainer, unchanged.
 *
 * The tiers are INDEPENDENT: a brand that wrote instructions but uploaded no
 * media keeps the glyph, and media with no instructions keeps the generic
 * sentence. Tier 3 is also where a broken or unrecognised media URL lands, so a
 * bad upload degrades to the explainer that shipped before this feature rather
 * than to a broken-image box.
 */
export const WhereToScanPanel = ({
  instructions,
  mediaUrl,
}: WhereToScanPanelProps) => {
  const [mediaFailed, setMediaFailed] = useState(false);

  // Keyed on the URL rather than on mount: ScanTag keeps this panel mounted while the
  // sheet is open, so a failure recorded for one certificate's media would otherwise
  // suppress the next certificate's.
  useEffect(() => setMediaFailed(false), [mediaUrl]);

  const kind = mediaUrl && !mediaFailed ? mediaKindOf(mediaUrl) : null;

  return (
    <Stack
      spacing={4}
      alignItems='center'
      sx={{
        textAlign: 'center',
        px: 2,
        pb: 2,
        /*
         * The safety valve for §52's fixed step height. ScanTag pins the stepped body
         * so moving between "scan" and here cannot resize the sheet; a brand who writes
         * instructions longer than the two lines that height was measured against
         * therefore has to go somewhere. It scrolls INSIDE the step rather than growing
         * the sheet — a little scrolling beats a sheet that jumps every time someone
         * asks where the tag is.
         *
         * `minHeight: 0` is required for that: this is a flex child, and a flex item's
         * default `min-height: auto` refuses to shrink below its content, which would
         * push the overflow back out to the sheet and defeat the fixed height.
         */
        minHeight: 0,
        overflowY: 'auto',
        width: '100%',
      }}
    >
      {kind === null && (
        <Box
          sx={theme => ({
            width: 72,
            height: 72,
            borderRadius: '50%',
            backgroundColor: theme.palette.grey[50],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.text.primary,
          })}
        >
          <SmartphoneNfc size={32} />
        </Box>
      )}

      {kind === 'image' && mediaUrl && (
        <MediaSlot>
          <FadeInImage
            src={mediaUrl}
            alt='Where the tag is on this item'
            // `FadeInImage` (not a bare <img>) is the app's image element — it
            // reveals rather than hard-cutting, and it already survives a
            // missing src. The `onError` it forwards is what lets this drawer
            // fall back to the glyph instead of leaving alt text in a box.
            //
            // The fade and the slot are complementary, not redundant: the slot
            // holds the SPACE so nothing moves, and the fade covers the arrival
            // so the photo resolves into that space instead of snapping into it.
            onError={() => setMediaFailed(true)}
            style={MEDIA_FIT}
          />
        </MediaSlot>
      )}

      {kind === 'video' && mediaUrl && (
        <MediaSlot>
        <Box
          component='video'
          key={mediaUrl}
          src={mediaUrl}
          controls
          playsInline
          muted
          loop
          /**
           * `preload='metadata'` is the load-bearing attribute here, not a
           * default worth copying past.
           *
           * There is no transcode step and no poster-frame generation
           * anywhere in this stack, so the brand's clip is served exactly as
           * uploaded — potentially tens of megabytes of phone video. This
           * sheet opens on a phone that is, by construction, standing in a
           * shop or a doorway on cellular data. `metadata` fetches the
           * header and stops, so the cost of OPENING the drawer is a few KB
           * and the file is only paid for if the user presses play.
           *
           * `auto` would spend the whole clip's bandwidth on everyone who
           * merely wondered where the tag was; autoplay would spend it AND
           * start motion nobody asked for. Both are wrong for the same
           * reason, which is why neither is here.
           */
          preload='metadata'
          onError={() => setMediaFailed(true)}
          // The neutral fill that used to live here moved to `MediaSlot`, which
          // now owns the reserved box for both media tiers.
          sx={MEDIA_FIT}
        />
        </MediaSlot>
      )}

      {/* The heading "Locate the tag on the item" was REMOVED. The sentence
          directly beneath it says the same thing and says it better: a brand's
          own instruction ("the tag sits under the rear binding plate") is
          strictly more specific, and the generic fallback already tells the user
          what to do. The sheet's header is also literally "Where to scan", so
          the heading was the third statement of one idea on a phone screen where
          vertical space is the scarce resource. */}
      <Typography
        variant='body2'
        sx={theme => ({ color: theme.palette.text.secondary, lineHeight: 1.5 })}
      >
        {/* Tier 2. The generic sentence is the fallback, not a prefix — a
            brand that says "It is sewn into the inside left seam" should not
            then be followed by the app repeating a vaguer version of the
            same instruction. */}
        {instructions?.trim() ||
          'Hold the top of your phone against the tag and keep it steady until the scan completes.'}
      </Typography>
    </Stack>
  );
};
